import React from 'react'
import styled, { css } from 'styled-components'
import { string, bool, func, object } from 'prop-types'
import { stripUnit } from 'polished'
import pick from 'lodash/pick'

import * as spacing from 'styles/spacing'
import { white, black, greyLight, borderColor } from 'styles/colors'
import { animationTime, animationCurve, borderRadius, square } from 'styles/global'
import { fontSizes } from 'styles/typography'
import Icon from 'components/Icon'

import CheckmarkSmall from 'icons/checkmark-small.svg'

const Container = styled.label`
  cursor: pointer;

  ${props =>
    props.disabled &&
    css`
      cursor: default;
    `}

  & + & {
    margin-top: ${spacing.medium};
  }
`

const Inner = styled.div`
  display: flex;
  align-items: flex-start;
`

const Control = styled.span`
  margin-right: ${stripUnit(spacing.small) * 1.5 + 'px'};
  flex-shrink: 0;

  ${props =>
    props.large &&
    css`
      margin-right: ${spacing.medium};
    `}

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
    `}
`

const Input = styled.input.attrs({ type: 'checkbox' })`
  display: none;
`

const Indicator = styled.div`
  ${square`20px`};
  position: relative;
  display: inline-block;
  vertical-align: top;
  border-radius: ${stripUnit(borderRadius) * 0.5 + 'px'};
  flex-shrink: 0;
  background-color: ${white};
  border: 1px solid ${borderColor};
  transition: background-color ${animationTime} ${animationCurve}, border-color ${animationTime} ${animationCurve};
  box-shadow: 0px 4px 14px rgba(0, 0, 79, 0.05);

  ${props =>
    props.large &&
    css`
      border-radius: ${borderRadius};
      ${square`32px`};
    `}

  ${Input}:checked + & {
    background-color: ${black};
    border-color: ${black};
  }
`

const Check = styled.div`
  display: flex;
  position: absolute;
  top: 50%;
  left: 50%;
  opacity: 0;
  transform: translate3d(-50%, -50%, 0) scale(0.6);
  transition: opacity ${animationTime} ${animationCurve}, transform ${animationTime} ${animationCurve};

  ${Input}:checked ~ ${Indicator} & {
    opacity: 1; /* Show icon when checkbox is checked */
    transform: translate3d(-50%, -50%, 0) scale(1);
  }
`

const Label = styled.div`
  text-align: left;
  margin-top: -2px; /* Align text with checkbox */

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
    `}
`

const Hint = styled.small`
  display: block;
  color: ${greyLight};
  font-size: ${fontSizes.tiny};
  padding-left: 30px;
`

const Checkbox = ({ label, hint, checked, large, disabled, field, ...rest }) => {
  const fieldProps = field || pick(rest, ['value', 'onChange', 'onBlur', 'name'])

  return (
    <Container disabled={disabled}>
      <Inner>
        <Control large={large} disabled={disabled}>
          <Input checked={checked} {...fieldProps} disabled={disabled} />
          <Indicator large={large}>
            <Check>
              <Icon icon={<CheckmarkSmall />} color="white" size={large ? 'medium' : null} />
            </Check>
          </Indicator>
        </Control>
        <Label disabled={disabled}>{label}</Label>
      </Inner>
      {hint && <Hint>{hint}</Hint>}
    </Container>
  )
}
Checkbox.propTypes = {
  label: string.isRequired,
  hint: string,
  checked: bool,
  large: bool,
  disabled: bool,
  onChange: func,
  name: string,
  field: object
}

export default Checkbox
